import React, { useEffect, useMemo, useState } from 'react';
import {
  Objective,
  ObjectiveStatus,
  Milestone,
  ObjectiveVersion,
  ChatMessage,
  Attachment,
  PMSRole,
} from '../../types/pms';
import ProgressBar from './ProgressBar';
import ProgressSlider from './ProgressSlider';
import MilestoneCard from './MilestoneCard';
import { getObjectiveStatusClass } from './ObjectiveCard';
import { OBJECTIVES_LOCKED_MESSAGE } from '../../services/pmsWorkflow';
import './ObjectiveDetail.css';

interface ObjectiveDetailProps {
  objective: Objective;
  milestones: Milestone[];
  versions: ObjectiveVersion[];
  chatMessages: ChatMessage[];
  attachments: Attachment[];
  /** Current user's PMS role */
  userRole: PMSRole;
  /** Current user ID */
  userId: string;
  /** Force read-only mode (e.g. historical cycle) */
  isReadOnly?: boolean;
  /** Action callbacks */
  onApprove?: () => void;
  onReturn?: (comments: string) => void;
  onReject?: (reason: string) => void;
  onEdit?: () => void;
  onAddMilestone?: () => void;
  onMilestoneClick?: (milestone: Milestone) => void;
  onSendMessage?: (content: string) => void;
  onUploadAttachment?: (file: File) => void;
  onDownloadAttachment?: (attachment: Attachment) => void;
  onProgressChange?: (value: number) => void;
}

/**
 * Format an ISO date string for display.
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Format an ISO date string with time for chat/version timestamps.
 */
function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return 'Not available';
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function parseHistoryValue(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (!((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']')))) {
    return value;
  }
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
}

function formatHistoryLabel(field: string): string {
  const labels: Record<string, string> = {
    managerFinalComment: 'Manager Comment',
    finalProgressPercentage: 'Final Progress',
    evaluationSummary: 'Evaluation',
    status: 'Status',
    statusAtChange: 'Status',
    progressPercentage: 'Progress',
    managerComments: 'Manager Comment',
    employeeComments: 'Employee Comment',
    title: 'Title',
    description: 'Description',
    measureOfSuccess: 'Measure of Success',
    weightage: 'Weightage',
  };
  return labels[field] || field.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').replace(/^./, (char) => char.toUpperCase());
}

function formatHistoryValue(field: string, value: unknown): string {
  const parsed = parseHistoryValue(value);
  if (parsed === undefined || parsed === null || parsed === '') return 'Not provided';
  if (typeof parsed === 'number' && (field.toLowerCase().includes('percentage') || field.toLowerCase().includes('progress'))) {
    return `${parsed}%`;
  }
  if (typeof parsed === 'string') {
    return parsed.replace(/_/g, ' ').replace(/^./, (char) => char.toUpperCase());
  }
  if (typeof parsed === 'object') {
    const entries = Object.entries(parsed as Record<string, unknown>);
    if (entries.length === 0) return 'No details';
    return entries
      .map(([key, entryValue]) => `${formatHistoryLabel(key)}: ${formatHistoryValue(key, entryValue)}`)
      .join(', ');
  }
  return String(parsed);
}

function getHistoryTitle(version: ObjectiveVersion): string {
  const action = version.action || 'Objective Updated';
  if (/review submitted/i.test(action)) return 'Review Submitted';
  if (/approved/i.test(action)) return 'Objective Approved';
  if (/returned|rejected/i.test(action)) return 'Objective Returned';
  if (/progress/i.test(action)) return 'Progress Updated';
  return action.replace(/_/g, ' ').replace(/^./, (char) => char.toUpperCase());
}

function getHistoryEntries(version: ObjectiveVersion): Array<{ label: string; value: string }> {
  const parsedNewValues = parseHistoryValue(version.newValues) as Record<string, unknown>;
  const newValues = parsedNewValues && typeof parsedNewValues === 'object' && !Array.isArray(parsedNewValues)
    ? parsedNewValues
    : {};
  const fields = version.changedFields.length > 0 ? version.changedFields : Object.keys(newValues);
  const entries = fields
    .filter((field) => Object.prototype.hasOwnProperty.call(newValues, field))
    .map((field) => ({
      label: formatHistoryLabel(field),
      value: formatHistoryValue(field, newValues[field]),
    }));

  if (version.statusAtChange) {
    entries.push({
      label: 'Status',
      value: formatHistoryValue('status', version.statusAtChange),
    });
  }

  return entries;
}

/**
 * Format file size in human-readable form.
 */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * ObjectiveDetail Component
 * Full objective view with milestones, chat thread, attachments, and version history.
 * Displays action buttons based on user role and objective status.
 * Requirements: 4.2, 5.5, 7.2, 18.2, 20.2
 */
const ObjectiveDetail: React.FC<ObjectiveDetailProps> = ({
  objective,
  milestones,
  versions,
  chatMessages,
  attachments,
  userRole,
  userId,
  isReadOnly = false,
  onApprove,
  onReturn,
  onReject,
  onEdit,
  onAddMilestone,
  onMilestoneClick,
  onSendMessage,
  onUploadAttachment,
  onDownloadAttachment,
  onProgressChange,
}) => {
  const [chatInput, setChatInput] = useState('');
  const [returnMode, setReturnMode] = useState(false);
  const [rejectMode, setRejectMode] = useState(false);
  const [returnComments, setReturnComments] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [draftProgress, setDraftProgress] = useState(objective.progressPercentage);
  const hasFinalEvaluation =
    objective.finalProgressPercentage !== undefined ||
    !!objective.managerFinalComment ||
    !!objective.evaluationSummary;

  useEffect(() => {
    setDraftProgress(objective.progressPercentage);
  }, [objective.id, objective.progressPercentage]);

  // Permission logic
  const isManager =
    userRole === PMSRole.MANAGER ||
    userRole === PMSRole.HR_ADMIN ||
    userRole === PMSRole.SUPER_ADMIN;
  const isOwner = userId === objective.employeeId;
  const canApproveReturn =
    !isReadOnly &&
    isManager &&
    objective.status === ObjectiveStatus.SUBMITTED &&
    !objective.isLocked;
  const canEdit =
    !isReadOnly &&
    ((isOwner &&
      [ObjectiveStatus.DRAFT, ObjectiveStatus.RETURNED].includes(objective.status) &&
      !objective.isLocked) ||
    (isManager && !objective.isLocked));
  const canChat = !isReadOnly && (isOwner || (isManager && userId === objective.managerId));
  const canEditProgress = useMemo(
    () =>
      !isReadOnly &&
      !objective.isLocked &&
      Boolean(onProgressChange) &&
      [ObjectiveStatus.DRAFT, ObjectiveStatus.RETURNED, ObjectiveStatus.APPROVED, ObjectiveStatus.IN_PROGRESS].includes(objective.status),
    [isReadOnly, objective.isLocked, objective.status, onProgressChange]
  );
  const progressReadOnlyMessage = canEditProgress ? undefined : 'Progress cannot be edited after submission.';

  const handleProgressChange = (value: number) => {
    const nextValue = Math.round(Math.min(100, Math.max(0, Number.isFinite(value) ? value : 0)));
    setDraftProgress(nextValue);
    if (canEditProgress) {
      onProgressChange?.(nextValue);
    }
  };

  const handleSendMessage = () => {
    if (!chatInput.trim() || !onSendMessage) return;
    onSendMessage(chatInput.trim());
    setChatInput('');
  };

  const handleReturnConfirm = () => {
    if (!returnComments.trim() || !onReturn) return;
    onReturn(returnComments.trim());
    setReturnMode(false);
    setReturnComments('');
  };

  const handleRejectConfirm = () => {
    if (!rejectReason.trim() || !onReject) return;
    onReject(rejectReason.trim());
    setRejectMode(false);
    setRejectReason('');
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && onUploadAttachment) {
      onUploadAttachment(file);
    }
    e.target.value = '';
  };

  return (
    <div className="pms-objective-detail">
      {/* Header */}
      <div className="pms-objective-detail__header">
        <div className="pms-objective-detail__title-row">
          {(objective.isLocked || isReadOnly) && (
            <span className="pms-objective-detail__lock" aria-label="Objective is locked">
              🔒
            </span>
          )}
          <h2 className="pms-objective-detail__title">{objective.title}</h2>
          <span
            className={`pms-objective-detail__status ${getObjectiveStatusClass(objective.status)}`}
          >
            {objective.status.replace('_', ' ')}
          </span>
        </div>
        {/*
          Why the panel's controls are unavailable, rather than leaving the reader to guess.
          isReadOnly is set from the HR cycle lock (see isObjectiveLockedByHR) as well as for a
          historical cycle, and every action below — approve, return, progress, chat, milestones
          — routes through it. Without this the panel simply arrived with its buttons absent.
        */}
        {isReadOnly && (
          <p className="pms-objective-detail__readonly-note">{OBJECTIVES_LOCKED_MESSAGE}</p>
        )}
      </div>

      <div className="pms-objective-detail__body">
        {/* Info Section */}
        <div className="pms-detail-section">
          <h3 className="pms-detail-section__title">Objective Information</h3>
          <div className="pms-detail-grid">
            <div className="pms-detail-field full-width">
              <span className="pms-detail-field__label">Description</span>
              <span className="pms-detail-field__value">{objective.description}</span>
            </div>
            <div className="pms-detail-field full-width">
              <span className="pms-detail-field__label">Measure of Success</span>
              <span className="pms-detail-field__value">{objective.measureOfSuccess}</span>
            </div>
            <div className="pms-detail-field">
              <span className="pms-detail-field__label">Start Date</span>
              <span className="pms-detail-field__value">{formatDate(objective.startDate)}</span>
            </div>
            <div className="pms-detail-field">
              <span className="pms-detail-field__label">End Date</span>
              <span className="pms-detail-field__value">{formatDate(objective.endDate)}</span>
            </div>
            <div className="pms-detail-field">
              <span className="pms-detail-field__label">Weightage</span>
              <span className="pms-detail-field__value">{objective.weightage}%</span>
            </div>
            <div className="pms-detail-field">
              <span className="pms-detail-field__label">Employee</span>
              <span className="pms-detail-field__value">{objective.employeeName}</span>
            </div>
            <div className="pms-detail-field full-width">
              <span className="pms-detail-field__label">Progress</span>
              <div className="pms-detail-progress-control" title={progressReadOnlyMessage}>
                <ProgressSlider
                  value={draftProgress}
                  onChange={canEditProgress ? handleProgressChange : undefined}
                  readOnly={!canEditProgress}
                  ariaLabel="Objective progress"
                />
              </div>
              {!canEditProgress && (
                <span className="pms-detail-progress-control__hint">
                  {progressReadOnlyMessage}
                </span>
              )}
            </div>
            {objective.employeeComments && (
              <div className="pms-detail-field full-width">
                <span className="pms-detail-field__label">Employee Comments</span>
                <span className="pms-detail-field__value">{objective.employeeComments}</span>
              </div>
            )}
            {objective.managerComments && (
              <div className="pms-detail-field full-width">
                <span className="pms-detail-field__label">Manager Comments</span>
                <span className="pms-detail-field__value">{objective.managerComments}</span>
              </div>
            )}
          </div>
        </div>

        {hasFinalEvaluation && (
          <div className="pms-detail-section">
            <h3 className="pms-detail-section__title">Final Objective Evaluation</h3>
            <div className="pms-detail-final-evaluation">
              <ProgressBar
                value={objective.finalProgressPercentage ?? objective.progressPercentage}
                label="Final Progress Percentage"
                size="sm"
              />
              <div className="pms-detail-final-evaluation__grid">
                <div className="pms-detail-final-evaluation__item">
                  <span className="pms-detail-final-evaluation__label">Evaluation Summary</span>
                  <strong>{objective.evaluationSummary || 'Not recorded'}</strong>
                </div>
                <div className="pms-detail-final-evaluation__item">
                  <span className="pms-detail-final-evaluation__label">Manager Final Comment</span>
                  <p>{objective.managerFinalComment || 'No final comment recorded.'}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Milestones Section */}
        <div className="pms-detail-section">
          <h3 className="pms-detail-section__title">
            Milestones ({milestones.length})
          </h3>
          {milestones.length > 0 ? (
            <div className="pms-detail-milestones-grid">
              {milestones.map((ms) => (
                <MilestoneCard
                  key={ms.id}
                  milestone={ms}
                  onClick={onMilestoneClick}
                />
              ))}
            </div>
          ) : (
            <p className="pms-detail-empty">No milestones yet.</p>
          )}
          {canEdit && onAddMilestone && (
            <button
              className="pms-detail-action-btn pms-detail-action-btn--edit"
              onClick={onAddMilestone}
              style={{ marginTop: 12 }}
            >
              + Add Milestone
            </button>
          )}
        </div>

        {/* Chat Section */}
        <div className="pms-detail-section">
          <h3 className="pms-detail-section__title">
            Discussion ({chatMessages.length})
          </h3>
          {chatMessages.length > 0 ? (
            <div className="pms-detail-chat-list">
              {chatMessages.map((msg) => (
                <div
                  key={msg.id}
                  className={`pms-detail-chat-msg ${msg.senderId === userId ? 'pms-detail-chat-msg--own' : ''}`}
                >
                  <div className="pms-detail-chat-msg__header">
                    <span className="pms-detail-chat-msg__sender">{msg.senderName}</span>
                    <span className="pms-detail-chat-msg__time">
                      {formatDateTime(msg.timestamp)}
                    </span>
                  </div>
                  <p className="pms-detail-chat-msg__content">{msg.content}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="pms-detail-empty">No messages yet.</p>
          )}
          {canChat && onSendMessage && (
            <div className="pms-detail-chat-input">
              <input
                type="text"
                className="pms-detail-chat-input__field"
                placeholder="Type a message..."
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSendMessage();
                }}
              />
              <button
                className="pms-detail-action-btn pms-detail-action-btn--edit"
                onClick={handleSendMessage}
                disabled={!chatInput.trim()}
              >
                Send
              </button>
            </div>
          )}
        </div>

        {/* Attachments Section */}
        <div className="pms-detail-section">
          <div className="pms-detail-section__header-row">
            <h3 className="pms-detail-section__title">
              Attachments ({attachments.length})
            </h3>
            {onUploadAttachment && (
              <label className="pms-detail-action-btn pms-detail-action-btn--edit pms-detail-upload-label">
                + Add Attachment
                <input
                  type="file"
                  className="pms-detail-upload-input"
                  onChange={handleFileUpload}
                />
              </label>
            )}
          </div>
          {attachments.length > 0 ? (
            <div className="pms-detail-attachments-list">
              {attachments.map((att) => (
                <div key={att.id} className="pms-detail-attachment-item">
                  <div className="pms-detail-attachment-item__info">
                    <span className="pms-detail-attachment-item__name">{att.fileName}</span>
                    <span className="pms-detail-attachment-item__meta">
                      {att.fileType.toUpperCase()} · {formatFileSize(att.fileSize)}
                    </span>
                  </div>
                  {onDownloadAttachment && (
                    <button
                      className="pms-detail-attachment-item__download"
                      onClick={() => onDownloadAttachment(att)}
                      aria-label={`Download ${att.fileName}`}
                    >
                      ⬇ Download
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="pms-detail-empty">No attachments.</p>
          )}
        </div>

        {/* Objective History Section */}
        <div className="pms-detail-section">
          <h3 className="pms-detail-section__title">
            Objective History ({versions.length})
          </h3>
          {versions.length > 0 ? (
            <div className="pms-detail-version-list">
              {versions.map((ver) => {
                const entries = getHistoryEntries(ver);
                return (
                  <div key={ver.id} className="pms-detail-version-item">
                    <div className="pms-detail-version-item__header">
                      <span className="pms-detail-version-item__user">{ver.changedByName}</span>
                      <span className="pms-detail-version-item__time">
                        {formatDateTime(ver.timestamp)}
                      </span>
                    </div>
                    <span className="pms-detail-version-item__fields">
                      {getHistoryTitle(ver)}
                    </span>
                    {entries.length > 0 ? (
                      <div className="pms-detail-version-item__entries">
                        {entries.map((entry, index) => (
                          <span key={`${entry.label}-${index}`} className="pms-detail-version-item__entry">
                            <strong>{entry.label}:</strong> {entry.value}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="pms-detail-version-item__status">
                        Details unavailable
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="pms-detail-empty">No objective history.</p>
          )}
        </div>
      </div>

      {/* Return/Reject Inline Modals */}
      {returnMode && (
        <div className="pms-detail-inline-modal">
          <h4 className="pms-detail-inline-modal__title">Return Objective</h4>
          <textarea
            className="pms-detail-inline-modal__textarea"
            placeholder="Provide comments for returning this objective (required)"
            value={returnComments}
            onChange={(e) => setReturnComments(e.target.value)}
          />
          <div className="pms-detail-inline-modal__actions">
            <button
              className="pms-detail-action-btn pms-detail-action-btn--cancel"
              onClick={() => {
                setReturnMode(false);
                setReturnComments('');
              }}
            >
              Cancel
            </button>
            <button
              className="pms-detail-action-btn pms-detail-action-btn--return"
              onClick={handleReturnConfirm}
              disabled={!returnComments.trim()}
            >
              Confirm Return
            </button>
          </div>
        </div>
      )}

      {rejectMode && (
        <div className="pms-detail-inline-modal">
          <h4 className="pms-detail-inline-modal__title">Reject Objective</h4>
          <textarea
            className="pms-detail-inline-modal__textarea"
            placeholder="Provide a reason for rejection (required)"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
          <div className="pms-detail-inline-modal__actions">
            <button
              className="pms-detail-action-btn pms-detail-action-btn--cancel"
              onClick={() => {
                setRejectMode(false);
                setRejectReason('');
              }}
            >
              Cancel
            </button>
            <button
              className="pms-detail-action-btn pms-detail-action-btn--reject"
              onClick={handleRejectConfirm}
              disabled={!rejectReason.trim()}
            >
              Confirm Reject
            </button>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="pms-detail-actions">
        {canApproveReturn && onApprove && (
          <button
            className="pms-detail-action-btn pms-detail-action-btn--approve"
            onClick={onApprove}
          >
            Approve
          </button>
        )}
        {canApproveReturn && onReturn && !returnMode && (
          <button
            className="pms-detail-action-btn pms-detail-action-btn--return"
            onClick={() => setReturnMode(true)}
          >
            Return
          </button>
        )}
        {canApproveReturn && onReject && !rejectMode && (
          <button
            className="pms-detail-action-btn pms-detail-action-btn--reject"
            onClick={() => setRejectMode(true)}
          >
            Reject
          </button>
        )}
        {canEdit && onEdit && (
          <button
            className="pms-detail-action-btn pms-detail-action-btn--edit"
            onClick={onEdit}
          >
            Edit
          </button>
        )}
      </div>
    </div>
  );
};

export default ObjectiveDetail;
